import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import { formatApiErrorDetail } from "@/lib/api";
import { Leaf, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAF8]">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Sustainable industrial facility"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#121A14]/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-2 mb-4">
            <Leaf className="h-6 w-6" />
            <span className="font-display font-bold text-xl">EnviSafe.ai</span>
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight">
            {t("auth.heroTitle")}
          </h2>
          <p className="mt-3 text-white/80 max-w-md">
            {t("auth.heroSub")}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden flex items-center gap-2">
              <div className="h-8 w-8 grid place-items-center bg-[#275C3F] rounded-sm">
                <Leaf className="h-5 w-5 text-white" />
              </div>
              <span className="font-display font-bold text-lg">EnviSafe.ai</span>
            </div>
            <div className="ml-auto"><LanguageToggle /></div>
          </div>
          <h1 className="font-display text-3xl font-bold text-[#121A14]">{t("auth.welcomeBack")}</h1>
          <p className="text-[#4B5950] mt-1 mb-6">{t("auth.signInSub")}</p>

          <button
            data-testid="google-login-button"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-2 border border-[#DCE3DD] bg-white rounded-sm py-2.5 text-sm font-medium hover:bg-[#EAF0EA] transition-all duration-200 active:scale-[0.98]"
          >
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" />
            {t("common.continueGoogle")}
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-[#DCE3DD]" />
            <span className="text-xs text-[#4B5950] uppercase tracking-wider">{t("common.or")}</span>
            <div className="h-px flex-1 bg-[#DCE3DD]" />
          </div>

          {error && (
            <div data-testid="login-error" className="mb-4 text-sm text-[#B84A22] bg-[#B84A22]/10 border border-[#B84A22]/20 rounded-sm px-3 py-2">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#121A14]">{t("common.email")}</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#121A14]">{t("common.password")}</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-[#275C3F] hover:bg-[#1E4731] text-white rounded-sm py-2.5 text-sm font-semibold transition-all duration-200 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("common.signIn")}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#4B5950] text-center">
            {t("common.noAccount")}{" "}
            <Link to="/register" data-testid="go-to-register-link" className="text-[#275C3F] font-semibold hover:underline">
              {t("common.createOne")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
